import { useEffect, useMemo, useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { bpToPercent } from '@redirector/shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { formatPercent } from '../lib/format.js';
import { BulkPasteDialog } from './BulkPasteDialog.js';
import { SplitBar } from './SplitBar.js';
import { WeightRow } from './WeightRow.js';
import { useSaveDestinations } from './mutations.js';
import {
  DEFAULT_TARGET_VIEW,
  viewTargets,
  type TargetSort,
  type TargetStatusFilter,
} from './targetView.js';
import { useWeightDraft, type Destination } from './useWeightDraft.js';

export type WeightEditorProps = {
  entryId: number;
  destinations: readonly Destination[];
  canEdit: boolean;
  /**
   * Reports whether the draft differs from what is saved.
   *
   * The draft deliberately lives and dies with this component — keeping a
   * per-row draft alive after the row closes would only trade a visible loss
   * for an invisible one. So the owner of the row's open/closed state is told
   * when a draft is at stake, and guards the unmount itself.
   */
  onDirtyChange?: (dirty: boolean) => void;
};

/** How long the "saved" confirmation stays on screen. */
const SAVED_NOTICE_MS = 2500;

/**
 * The destination split for one URL.
 *
 * The component owns its own section heading, which is why the URL page does
 * not wrap it in one: the actions belong beside that heading, at the top of
 * the editor. They used to sit under the rows, which was fine for three
 * destinations and wrong for thirty — Save was below the fold on exactly the
 * splits that take the most editing.
 */
export function WeightEditor({ entryId, destinations, canEdit, onDirtyChange }: WeightEditorProps) {
  const draft = useWeightDraft(destinations);
  const save = useSaveDestinations(entryId);
  const [pasting, setPasting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /* The view over the rows, which is display only: it never touches the draft,
     so filtering a list of forty down to the two being changed cannot lose an
     edit made to the other thirty-eight. */
  const [view, setView] = useState(DEFAULT_TARGET_VIEW);
  const visible = useMemo(() => viewTargets(draft.rows, view), [draft.rows, view]);
  const filtering = view.query.trim() !== '' || view.status !== 'all';

  /* Adding puts a blank row at the top of the draft, so the view has to be
     showing the top of the draft for that to be visible. Anything else —
     adding a row while sorted by percent, where a 0% row lands last, or while
     a filter it cannot match is on — looks like the button did nothing. */
  function addAndReveal() {
    setView(DEFAULT_TARGET_VIEW);
    draft.addRow();
  }

  useEffect(() => {
    if (onDirtyChange === undefined) return;
    onDirtyChange(draft.isDirty);
    // Unmounting can only happen once the owner has cleared the guard, so
    // reporting clean on the way out cannot strand a stale warning.
    return () => onDirtyChange(false);
  }, [draft.isDirty, onDirtyChange]);

  const remainingPct = bpToPercent(draft.remainingBp);
  const balanced = draft.remainingBp === 0;

  async function commit() {
    setFailure(null);
    setSaved(false);
    try {
      await save.mutateAsync(draft.toPayload());
      setSaved(true);
      setTimeout(() => setSaved(false), SAVED_NOTICE_MS);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not save the split');
    }
  }

  return (
    <section aria-labelledby="targets-heading" className="flex flex-col gap-(--space-4)">
      <div className="flex flex-wrap items-start justify-between gap-(--space-4)">
        <div>
          <h2 id="targets-heading" className="text-[length:var(--text-lead)] font-bold">
            Targets
          </h2>
          <p className="mt-(--space-1) max-w-[70ch] text-[length:var(--text-small)] font-medium text-ink-muted">
            Every request to this URL is sent to one of these, chosen by weight. Incoming query
            parameters are forwarded to whichever target is picked.
          </p>
        </div>

        {canEdit && (
          <div className="flex shrink-0 flex-wrap items-center gap-(--space-2)">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setView(DEFAULT_TARGET_VIEW);
                setPasting(true);
              }}
            >
              Import
            </Button>
            <Button size="sm" variant="ghost" onClick={addAndReveal}>
              Add destination
            </Button>
            <Button size="sm" variant="ghost" onClick={draft.distributeAll}>
              Distribute evenly
            </Button>
            <Button size="sm" variant="secondary" disabled={!draft.isDirty} onClick={draft.reset}>
              Reset
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!draft.isDirty || !draft.isValid || save.isPending}
              onClick={commit}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {/*
        The allocation state, directly under the actions that change it.
        Two things can block a save and each says which it is: the total being
        off, and an invalid URL (the row says that one itself). A destination
        at zero is reported beside them but blocks nothing — it is parked.
      */}
      <div className="flex flex-wrap items-center gap-(--space-4)">
        <p
          className={
            balanced
              ? 'text-[length:var(--text-small)] font-medium text-ok'
              : 'text-[length:var(--text-small)] font-medium text-warn'
          }
          aria-live="polite"
        >
          Remaining to allocate:{' '}
          <strong className="font-bold">{formatPercent(remainingPct)}</strong>
          {!balanced &&
            (draft.remainingBp > 0
              ? ' — give it out, or press Distribute evenly'
              : ' — take that much back before saving')}
        </p>

        {/* Reported, not refused. A destination at 0% saves fine — it is parked,
            keeping its id and its traffic history — so this says what will
            happen rather than what is forbidden. */}
        {draft.unallocated > 0 && (
          <p className="text-[length:var(--text-small)] font-medium text-warn" aria-live="polite">
            {draft.unallocated === 1
              ? '1 destination is at 0% and takes no traffic'
              : `${draft.unallocated} destinations are at 0% and take no traffic`}
            .
          </p>
        )}
      </div>

      <SplitBar rows={draft.rows} effectivePct={draft.effectivePct} />

      {draft.hasDisabled && (
        <Callout tone="warn">
          Paused destinations take no traffic at all. Their configured share is redistributed across
          the live ones, so the live column below differs from the configured percentages.
        </Callout>
      )}

      {failure !== null && <Callout tone="danger">{failure}</Callout>}

      {/*
        Filter and sort, directly above the rows they act on.
        Worth having at all because a split is not always three rows: an entry
        with forty destinations was a wall of identical controls with no way to
        ask "which of these is taking the traffic" or "where did that domain
        go" short of reading every row. Both are display only — see
        `targetView.ts` — so neither can touch what gets saved.
      */}
      <div className="flex flex-wrap items-end gap-(--space-3)">
        <div className="flex flex-col gap-(--space-1)">
          <label
            htmlFor="targets-filter"
            className="text-[length:var(--text-small)] font-bold text-ink"
          >
            Filter
          </label>
          <div className="relative">
            <SearchIcon
              aria-hidden="true"
              className="pointer-events-none absolute left-(--space-2) top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
            />
            <input
              id="targets-filter"
              type="search"
              spellCheck={false}
              autoComplete="off"
              placeholder="Destination URL"
              className="h-(--control-height) w-[16rem] max-w-full rounded-(--radius-md) border border-input bg-card pl-(--space-6) pr-(--space-3) text-[length:var(--text-small)] text-ink placeholder:text-ink-subtle"
              value={view.query}
              onChange={(event) =>
                setView((current) => ({ ...current, query: event.target.value }))
              }
            />
          </div>
        </div>

        <div className="flex flex-col gap-(--space-1)">
          <label
            htmlFor="targets-status"
            className="text-[length:var(--text-small)] font-bold text-ink"
          >
            Show
          </label>
          <Select
            value={view.status}
            onValueChange={(next) =>
              setView((current) => ({ ...current, status: next as TargetStatusFilter }))
            }
          >
            <SelectTrigger id="targets-status" className="w-[10rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="live">Live only</SelectItem>
              <SelectItem value="paused">Paused only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-(--space-1)">
          <label
            htmlFor="targets-sort"
            className="text-[length:var(--text-small)] font-bold text-ink"
          >
            Sort by
          </label>
          <Select
            value={view.sort}
            onValueChange={(next) =>
              setView((current) => ({ ...current, sort: next as TargetSort }))
            }
          >
            <SelectTrigger id="targets-sort" className="w-[14rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="order">Added order</SelectItem>
              <SelectItem value="percent-desc">Percent, high to low</SelectItem>
              <SelectItem value="percent-asc">Percent, low to high</SelectItem>
              <SelectItem value="url-asc">URL, A to Z</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filtering && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setView((current) => ({ ...current, query: '', status: 'all' }))}
          >
            Clear filter
          </Button>
        )}
      </div>

      {/* Only while a filter is narrowing the list. Unfiltered, the rows are
          the count, and a line saying so on every visit is noise — but a
          filtered editor MUST say it is hiding rows, or the operator reads a
          split that does not add up and blames the arithmetic. */}
      {filtering && (
        <p role="status" className="text-[length:var(--text-small)] text-ink-muted">
          Showing {visible.length} of {draft.rows.length} destinations. Hidden rows are still saved.
        </p>
      )}

      <div role="group" aria-label="Destination weights">
        {visible.map(({ row, index }) => (
          <WeightRow
            key={row.key}
            row={row}
            /* The DRAFT index, not the position on screen: it picks the colour
               that matches this row's band in the split bar above, which does
               not reorder when the list is sorted. */
            index={index}
            canEdit={canEdit}
            effectivePct={draft.effectivePct(row.key)}
            onWeight={(percent) => draft.setWeightPct(row.key, percent)}
            onUrl={(url) => draft.setUrl(row.key, url)}
            onToggleLock={() => draft.toggleLock(row.key)}
            onToggleEnabled={() => draft.toggleEnabled(row.key)}
            onRemove={() => draft.removeRow(row.key)}
          />
        ))}
      </div>

      {visible.length === 0 && (
        <p className="text-[length:var(--text-small)] text-ink-muted">
          No destination matches this filter.
        </p>
      )}

      {draft.isDirty && (
        <p className="text-[length:var(--text-small)] text-warn" data-testid="weight-draft-dirty">
          <strong className="font-bold">Unsaved changes.</strong> Live traffic still follows the
          saved split until you save.
        </p>
      )}

      <p role="status" aria-live="polite" className="text-[length:var(--text-small)] text-ok">
        {saved ? 'Saved. Live traffic is following the new split.' : ''}
      </p>

      <BulkPasteDialog open={pasting} onClose={() => setPasting(false)} onAdd={draft.addMany} />
    </section>
  );
}
